import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import BiotechIcon from '@mui/icons-material/Biotech';
import ModuleHeader from './components/ModuleHeader';
import { API_URL } from './config';
import { useAuth } from './AuthContext';
import MuralDivergencias, { EstadoAuditoria, RespostaAuditoria } from './MuralDivergencias';

// Quadro de Positividade por Tipo de Deposito - versao visual 3 (Sessao 27, 17/09/2026).
// Somente leitura. Desenho TDAH-friendly aprovado pelo Walber:
//  - tabela em "folha clara" com numeros grandes (alto contraste, espelha o papel);
//  - cada familia de deposito com cor propria (paleta Okabe-Ito, segura para daltonismo);
//  - totais em blocos de cor forte; passos numerados; secoes com faixa lateral;
//  - aviso verde "pode transcrever" / amarelo "espere";
//  - modo "passar para o papel" com marca-texto amarelo e marcacao local que expira se o numero mudar.
// Vermelho/laranja evitados: reservados a alerta epidemiologico no TOVIS.

type Dep = { a1: number; a2: number; b: number; c: number; d1: number; d2: number; e: number };
const TIPOS: (keyof Dep)[] = ['a1', 'a2', 'b', 'c', 'd1', 'd2', 'e'];
const INICIO_FAMILIA = new Set<keyof Dep>(['a1', 'b', 'c', 'd1', 'e']);

type Familia = 'A' | 'B' | 'C' | 'D' | 'E';
const FAMILIA_DO_TIPO: Record<keyof Dep, Familia> = { a1: 'A', a2: 'A', b: 'B', c: 'C', d1: 'D', d2: 'D', e: 'E' };
const FAM: Record<Familia, { cab: string; cabTx: string; cel: string; nome: string; span: number }> = {
  A: { cab: '#56B4E9', cabTx: '#062a43', cel: '#e3f3fc', nome: 'água', span: 2 },
  B: { cab: '#009E73', cabTx: '#ffffff', cel: '#dcf3ec', nome: 'móveis', span: 1 },
  C: { cab: '#F0E442', cabTx: '#3d3a00', cel: '#fbf8d6', nome: 'fixos', span: 1 },
  D: { cab: '#CC79A7', cabTx: '#ffffff', cel: '#f7e6ef', nome: 'remoção', span: 2 },
  E: { cab: '#A6761D', cabTx: '#ffffff', cel: '#f3ead9', nome: 'naturais', span: 1 },
};
const FAMILIAS: Familia[] = ['A', 'B', 'C', 'D', 'E'];
const LEGENDA_FAM: Record<Familia, string> = {
  A: 'A1/A2 armazenamento de água',
  B: 'B pequenos depósitos móveis',
  C: 'C depósitos fixos',
  D: 'D1/D2 passíveis de remoção',
  E: 'E depósitos naturais',
};

type Total = 'pos' | 'imo' | 'qt';
const TOT: Record<Total, { bg: string; tx: string; rotulo: string; legenda: string }> = {
  pos: { bg: '#0072B2', tx: '#ffffff', rotulo: 'Dep+', legenda: 'Dep+ depósitos positivos' },
  imo: { bg: '#6A3D9A', tx: '#ffffff', rotulo: 'Imóv+', legenda: 'Imóv+ imóveis positivos (sem PE)' },
  qt: { bg: '#1B7F5B', tx: '#ffffff', rotulo: 'Qt+', legenda: 'Qt+ quarteirões positivos (1 por semana)' },
};

const K = {
  pagina: '#eaf0f7', tinta: '#10233f', sub: '#4a5d75', grade: '#8a9bb0', forte: '#10233f',
  zero: '#b9c3cf', idBg: '#eef2f7', folha: '#ffffff', bordaFolha: '#cfd8e3',
  marca: 'rgba(255,230,0,0.55)', marcaBorda: '#ffe600',
  filtroBg: '#dcebfa', filtroBorda: '#0072B2',
  okBg: '#dcf3ec', okBorda: '#1B7F5B', okTx: '#0b3d2c',
  espereBg: '#fff4c2', espereBorda: '#c9a400', espereTx: '#4d3d00',
  infoBg: '#e8edf4', infoBorda: '#8a9bb0',
  erroBg: '#f6e3e6', erroBorda: '#9b4a5a', erroTx: '#4a1622',
};
const MONO = "'JetBrains Mono', ui-monospace, monospace";

interface Boletim {
  id: number; data: string; agente: string | null; quarteirao_trabalhado: string | null;
  dep: Dep; dep_pos: number; imov_pos: number; quarteiroes_positivos: string[];
}
interface Totais { dep: Dep; dep_pos: number; imov_pos: number; qt_pos: number; }
interface Linha extends Totais {
  se: number; se_inicio: string | null; se_fim: string | null;
  quarteiroes: string[]; total_boletins_semana: number; boletins: Boletim[];
}
interface CicloBloco { ciclo_id: number; ciclo_nome: string; ciclo_numero: number | null; linhas: Linha[]; total: Totais; }
interface Dados {
  area: { id: number; nome: string } | null;
  ciclos: CicloBloco[];
  total_geral: Totais;
  amostras: { coletadas: number; sem_resultado: number; congeladas: number };
  corte: { data: string; se: number };
  gerado_em: string;
}
interface Opcoes {
  corte: { data: string; se: number };
  areas: { id: number; nome: string }[];
  ciclos: { id: number; nome: string; ativo: boolean }[];
  semanas: { se: number; inicio: string | null; fim: string | null }[];
  ultimo_boletim: { data: string; se: number } | null;
}
interface Props { onVoltar: () => void; onAbrirFAD01?: (id: number) => void; }

type LinhaTabela = {
  chave: string;
  tipo: 'semana' | 'total' | 'geral';
  rotuloCiclo: string;
  rotuloSe: string;
  periodo: string;
  valores: Totais;
  semana?: Linha;
};

const fmtData = (s?: string | null) => (s ? `${s.slice(8, 10)}/${s.slice(5, 7)}` : '');
const val = (n: number) => (n > 0 ? String(n) : '–');

function msgErro(e: unknown): string {
  if (axios.isAxiosError(e)) {
    if (e.response?.status === 401) return 'Sua sessão expirou. Entre de novo no sistema.';
    const d = (e.response?.data as { detail?: unknown } | undefined)?.detail;
    if (typeof d === 'string') return d;
  }
  return 'Não foi possível carregar o quadro. Tente de novo.';
}

// Filtros lembrados na aba (Bloco F1): ao voltar do FAD-01 a tela reabre no mesmo filtro.
const CHAVE_FILTROS = 'tovis-qp-filtros';
type FiltrosSalvos = { areaId?: string; cicloId?: string; seIni?: string; seFim?: string };
function lerFiltros(): FiltrosSalvos | null {
  try {
    const r = sessionStorage.getItem(CHAVE_FILTROS);
    const p: unknown = r ? JSON.parse(r) : null;
    return p && typeof p === 'object' ? (p as FiltrosSalvos) : null;
  } catch {
    return null;
  }
}
function gravarFiltros(f: FiltrosSalvos) {
  try {
    sessionStorage.setItem(CHAVE_FILTROS, JSON.stringify(f));
  } catch {
    // navegador sem armazenamento: segue sem lembrar os filtros
  }
}

type Auditoria = { estado: EstadoAuditoria; resposta: RespostaAuditoria | null; tentativaEm?: string };

// Junta as respostas da conferencia (uma por ciclo) numa so.
function juntarRespostas(lista: RespostaAuditoria[]): RespostaAuditoria {
  const base: RespostaAuditoria = {
    grupos: [], auditado_em: '', boletins_conferidos: 0,
    amostras: { coletadas: 0, sem_resultado: 0, congeladas: 0 },
    totais: { travas: 0, avisos: 0, boletins_com_trava: 0, boletins_com_aviso: 0,
      boletins_com_amostra_sem_resultado: 0, semanas_com_recusa: 0 },
    explicacao_amostra: lista[0]?.explicacao_amostra ?? {},
  };
  lista.forEach(r => {
    base.grupos = base.grupos.concat(r.grupos || []);
    base.boletins_conferidos += r.boletins_conferidos || 0;
    (Object.keys(base.amostras) as (keyof typeof base.amostras)[]).forEach(k => {
      base.amostras[k] += r.amostras?.[k] || 0;
    });
    (Object.keys(base.totais) as (keyof typeof base.totais)[]).forEach(k => {
      base.totais[k] += r.totais?.[k] || 0;
    });
    if (r.auditado_em && r.auditado_em > base.auditado_em) base.auditado_em = r.auditado_em;
  });
  return base;
}

const chaveMarcas = (a: string, c: string, si: string, sf: string) =>
  `tovis_qp_marcas:${a}:${c || 'todos'}:${si || 'ini'}:${sf || 'fim'}`;
function lerMarcas(k: string): string[] {
  try {
    const r = localStorage.getItem(k);
    const p: unknown = r ? JSON.parse(r) : [];
    return Array.isArray(p) ? p.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}
function gravarMarcas(k: string, m: string[]) {
  try {
    localStorage.setItem(k, JSON.stringify(m));
  } catch {
    // navegador sem armazenamento: segue sem guardar as marcas
  }
}
// A marca guarda os numeros da linha: se o numero mudar, a marca deixa de valer sozinha.
const assinatura = (l: LinhaTabela) =>
  `${l.chave}|${TIPOS.map(t => l.valores.dep[t]).join('.')}|${l.valores.dep_pos}|${l.valores.imov_pos}|${l.valores.qt_pos}`;

const BORDA = `1.5px solid ${K.grade}`;
const BORDA_FAMILIA = `4px solid ${K.forte}`;
const TH: React.CSSProperties = {
  padding: '7px 3px', border: BORDA, fontSize: 14, fontWeight: 700, textAlign: 'center',
  color: K.tinta, lineHeight: 1.15, whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word',
  verticalAlign: 'middle',
};
const TD: React.CSSProperties = {
  padding: '10px 3px', border: BORDA, textAlign: 'center', color: K.tinta,
  fontFamily: MONO, fontSize: 20, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
};
const CARD: React.CSSProperties = { borderRadius: 12, padding: '12px 14px', marginBottom: 12 };
const SELECT: React.CSSProperties = {
  width: '100%', padding: '9px 10px', borderRadius: 8, border: '2px solid #9fb3cc',
  background: '#ffffff', color: K.tinta, fontSize: 15,
};
const ROTULO: React.CSSProperties = { display: 'block', fontSize: 13, fontWeight: 700, color: K.tinta, marginBottom: 5 };
const BOTAO: React.CSSProperties = {
  padding: '9px 14px', borderRadius: 8, border: `2px solid ${K.tinta}`, background: '#ffffff',
  color: K.tinta, fontSize: 14, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
};
const BOTAO_PRI: React.CSSProperties = { ...BOTAO, background: '#0072B2', borderColor: '#0072B2', color: '#ffffff' };
const CHIP: React.CSSProperties = { fontSize: 13, fontWeight: 700, padding: '4px 10px', borderRadius: 999, display: 'inline-block' };

function celDep(t: keyof Dep, n: number, extra: React.CSSProperties): React.CSSProperties {
  return {
    ...TD, ...extra,
    background: FAM[FAMILIA_DO_TIPO[t]].cel,
    color: n > 0 ? K.tinta : K.zero,
    fontWeight: n > 0 ? 700 : 400,
    borderLeft: INICIO_FAMILIA.has(t) ? BORDA_FAMILIA : BORDA,
  };
}
function celTot(g: Total, n: number, extra: React.CSSProperties, familia: boolean): React.CSSProperties {
  // Na linha em foco, o bloco colorido ganha um aro amarelo (o marca-texto por cima mancharia a cor).
  const { boxShadow, ...resto } = extra;
  return {
    ...TD, ...resto,
    boxShadow: boxShadow ? `inset 0 0 0 4px ${K.marcaBorda}` : undefined,
    background: TOT[g].bg,
    color: TOT[g].tx,
    fontWeight: n > 0 ? 800 : 400,
    borderLeft: familia ? BORDA_FAMILIA : BORDA,
  };
}

const CSS_IMPRESSAO = `
@media print {
  .qp-noprint { display: none !important; }
  .qp-raiz { background: #fff !important; padding: 0 !important; }
  .qp-folha { box-shadow: none !important; border: none !important; padding: 0 !important; }
  .qp-raiz table { border-color: #000 !important; }
  .qp-raiz th, .qp-raiz td { background: #fff !important; color: #000 !important; border-color: #000 !important; box-shadow: none !important; }
  .qp-titulo-impressao { display: block !important; }
}
`;

export default function QuadroPositividade({ onVoltar, onAbrirFAD01 }: Props) {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [opcoes, setOpcoes] = useState<Opcoes | null>(null);
  const [areaId, setAreaId] = useState('');
  const [cicloId, setCicloId] = useState('');
  const [seIni, setSeIni] = useState('');
  const [seFim, setSeFim] = useState('');
  const [dados, setDados] = useState<Dados | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [filtrosAbertos, setFiltrosAbertos] = useState(true);
  const [modo, setModo] = useState(false);
  const [idx, setIdx] = useState(0);
  const [marcas, setMarcas] = useState<string[]>([]);
  const [detalhe, setDetalhe] = useState<string | null>(null);
  const [filtrosProntos, setFiltrosProntos] = useState(false);

  useEffect(() => {
    axios.get<Opcoes>(`${API_URL}/api/quadro-positividade/opcoes`, { headers })
      .then(r => {
        setOpcoes(r.data);
        const ativo = r.data.ciclos.find(c => c.ativo);
        if (ativo) setCicloId(String(ativo.id));
        if (r.data.areas.length === 1) setAreaId(String(r.data.areas[0].id));
        // Filtros lembrados: so aplica o que ainda existe nas opcoes.
        const salvo = lerFiltros();
        if (salvo) {
          if (salvo.areaId && r.data.areas.some(a => String(a.id) === salvo.areaId)) setAreaId(salvo.areaId);
          if (salvo.cicloId === '' || (salvo.cicloId && r.data.ciclos.some(c => String(c.id) === salvo.cicloId)))
            setCicloId(salvo.cicloId);
          const temSe = (v?: string) => !!v && r.data.semanas.some(s => String(s.se) === v);
          if (salvo.seIni === '' || temSe(salvo.seIni)) setSeIni(salvo.seIni ?? '');
          if (salvo.seFim === '' || temSe(salvo.seFim)) setSeFim(salvo.seFim ?? '');
        }
        setFiltrosProntos(true);
      })
      .catch(e => setErro(msgErro(e)));
  }, [headers]);

  useEffect(() => {
    if (filtrosProntos) gravarFiltros({ areaId, cicloId, seIni, seFim });
  }, [filtrosProntos, areaId, cicloId, seIni, seFim]);

  const buscar = useCallback(() => {
    setDetalhe(null);
    if (!areaId) { setDados(null); return; }
    if (seIni && seFim && Number(seIni) > Number(seFim)) {
      setDados(null); setErro('A SE inicial não pode ser maior que a final.'); return;
    }
    setCarregando(true); setErro('');
    const params: Record<string, string> = { area_id: areaId };
    if (cicloId) params.ciclo_id = cicloId;
    if (seIni) params.se_inicio = seIni;
    if (seFim) params.se_fim = seFim;
    axios.get<Dados>(`${API_URL}/api/quadro-positividade/dados`, { headers, params })
      .then(r => setDados(r.data))
      .catch(e => { setDados(null); setErro(msgErro(e)); })
      .finally(() => setCarregando(false));
  }, [areaId, cicloId, seIni, seFim, headers]);

  useEffect(() => { buscar(); }, [buscar]);

  // Auditoria do modulo (Sessao 29 + Bloco F1) — so leitura, nao muda nenhum numero do quadro.
  // Mesmo recorte da tela: ciclo (ou todos os ciclos listados), area obrigatoria, SEs exibidas.
  // Qualquer falha vira 'falhou' (cinza) — nunca verde.
  const [auditoria, setAuditoria] = useState<Auditoria>({ estado: 'carregando', resposta: null });
  const rodada = useRef(0);
  const auditar = useCallback(() => {
    const minha = ++rodada.current;
    if (!opcoes) { setAuditoria({ estado: 'carregando', resposta: null }); return; }
    if (!areaId) { setAuditoria({ estado: 'sem_area', resposta: null }); return; }
    setAuditoria({ estado: 'carregando', resposta: null });
    const ciclosAlvo = cicloId ? [cicloId] : opcoes.ciclos.map(c => String(c.id));
    const seInicio = seIni || (opcoes.corte?.se != null ? String(opcoes.corte.se) : '');
    Promise.all(ciclosAlvo.map(c => {
      const params: Record<string, string> = { ciclo_id: c, area_id: areaId };
      if (seInicio) params.se_ini = seInicio;
      if (seFim) params.se_fim = seFim;
      return axios.get<RespostaAuditoria>(`${API_URL}/api/fad01/conferencia-laboratorio`, { headers, params })
        .then(r => {
          if (!r.data || !Array.isArray(r.data.grupos) || !r.data.totais || !r.data.amostras) throw new Error('resposta incompleta');
          return r.data;
        });
    }))
      .then(lista => { if (minha === rodada.current) setAuditoria({ estado: 'ok', resposta: juntarRespostas(lista) }); })
      .catch(() => {
        const agora = new Date();
        const local = `${agora.getFullYear()}-${String(agora.getMonth() + 1).padStart(2, '0')}-${String(agora.getDate()).padStart(2, '0')}`
          + `T${String(agora.getHours()).padStart(2, '0')}:${String(agora.getMinutes()).padStart(2, '0')}`;
        if (minha === rodada.current) setAuditoria({ estado: 'falhou', resposta: null, tentativaEm: local });
      });
  }, [opcoes, cicloId, areaId, seIni, seFim, headers]);

  useEffect(() => { auditar(); }, [auditar]);

  const refazer = useCallback(() => { auditar(); buscar(); }, [auditar, buscar]);

  const audTravas = auditoria.resposta?.totais.travas ?? 0;
  const audRecusas = auditoria.resposta?.totais.semanas_com_recusa ?? 0;
  const audAvisos = auditoria.resposta?.totais.avisos ?? 0;

  const linhas: LinhaTabela[] = useMemo(() => {
    if (!dados) return [];
    const out: LinhaTabela[] = [];
    dados.ciclos.forEach(c => {
      const rot = c.ciclo_numero ? `${c.ciclo_numero}º` : c.ciclo_nome;
      c.linhas.forEach((l, i) => out.push({
        chave: `c${c.ciclo_id}-se${l.se}`, tipo: 'semana', rotuloCiclo: i === 0 ? rot : '',
        rotuloSe: String(l.se), periodo: `${fmtData(l.se_inicio)} a ${fmtData(l.se_fim)}`, valores: l, semana: l,
      }));
      out.push({ chave: `c${c.ciclo_id}-total`, tipo: 'total', rotuloCiclo: `Total ${rot} ciclo`, rotuloSe: '', periodo: '', valores: c.total });
    });
    if (dados.ciclos.length) {
      out.push({ chave: 'geral', tipo: 'geral', rotuloCiclo: 'TOTAL GERAL', rotuloSe: '', periodo: '', valores: dados.total_geral });
    }
    return out;
  }, [dados]);

  const kMarcas = chaveMarcas(areaId, cicloId, seIni, seFim);
  useEffect(() => { setMarcas(lerMarcas(kMarcas)); }, [kMarcas]);
  useEffect(() => { setIdx(0); }, [dados]);

  const marcada = useCallback((l: LinhaTabela) => marcas.includes(assinatura(l)), [marcas]);

  const alternarMarca = useCallback((l: LinhaTabela) => {
    const s = assinatura(l);
    const sem = marcas.filter(m => !m.startsWith(`${l.chave}|`));
    const novo = marcas.includes(s) ? sem : [...sem, s];
    setMarcas(novo);
    gravarMarcas(kMarcas, novo);
  }, [marcas, kMarcas]);

  const passei = useCallback(() => {
    const l = linhas[idx];
    if (!l) return;
    if (!marcas.includes(assinatura(l))) alternarMarca(l);
    setIdx(i => Math.min(i + 1, linhas.length - 1));
  }, [linhas, idx, marcas, alternarMarca]);

  const limparMarcas = () => { setMarcas([]); gravarMarcas(kMarcas, []); };

  useEffect(() => {
    if (!modo) return undefined;
    const h = (ev: KeyboardEvent) => {
      if (ev.key === 'ArrowDown') { ev.preventDefault(); setIdx(i => Math.min(i + 1, linhas.length - 1)); }
      else if (ev.key === 'ArrowUp') { ev.preventDefault(); setIdx(i => Math.max(i - 1, 0)); }
      else if (ev.key === 'Enter') { ev.preventDefault(); passei(); }
      else if (ev.key === 'Escape') { setModo(false); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [modo, linhas.length, passei]);

  const areaNome = opcoes?.areas.find(a => String(a.id) === areaId)?.nome || dados?.area?.nome || '';
  const cicloNome = opcoes?.ciclos.find(c => String(c.id) === cicloId)?.nome || 'Todos os ciclos';
  const faixaSe = seIni || seFim
    ? `SE ${seIni || opcoes?.corte.se || ''} a ${seFim || 'atual'}`
    : `A partir da SE ${opcoes?.corte.se ?? ''}`;
  const passadas = linhas.filter(marcada).length;
  const semanaDetalhe = linhas.find(l => l.chave === detalhe)?.semana;
  const semAreas = !!opcoes && opcoes.areas.length === 0;
  const temTabela = !carregando && linhas.length > 0;

  const renderValores = (v: Totais, extra: React.CSSProperties) => (
    <>
      {TIPOS.map(t => <td key={t} style={celDep(t, v.dep[t], extra)}>{val(v.dep[t])}</td>)}
      <td style={celTot('pos', v.dep_pos, extra, true)}>{val(v.dep_pos)}</td>
      <td style={celTot('imo', v.imov_pos, extra, false)}>{val(v.imov_pos)}</td>
      <td style={celTot('qt', v.qt_pos, extra, false)}>{val(v.qt_pos)}</td>
    </>
  );

  const passo = (n: number, cor: string, fundo: string, texto: string) => (
    <div style={{ background: fundo, borderRadius: 10, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10, color: K.tinta, fontSize: 14, fontWeight: 700 }}>
      <span style={{ width: 28, height: 28, borderRadius: '50%', background: cor, color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none', fontSize: 15 }}>{n}</span>
      {texto}
    </div>
  );

  return (
    <div className="qp-raiz" style={{ minHeight: '100vh', background: K.pagina, color: K.tinta, paddingBottom: 40 }}>
      <style>{CSS_IMPRESSAO}</style>
      <div className="qp-noprint">
        <ModuleHeader
          titulo="Quadro de Positividade"
          subtitulo="Positivos por tipo de depósito, por área"
          icone={<BiotechIcon />}
          accentColor="#7F77DD"
          onVoltar={onVoltar}
          chips={[
            { label: 'Área', value: areaNome || '—' },
            { label: 'Ciclo', value: cicloNome },
            { label: 'Semanas', value: faixaSe },
          ]}
        />
      </div>

      <div style={{ maxWidth: 1120, margin: '0 auto', padding: '16px 20px' }}>
        <MuralDivergencias
          estado={auditoria.estado}
          resposta={auditoria.resposta}
          amostrasQuadro={dados ? dados.amostras : null}
          onRefazer={refazer}
          onAbrirFAD01={onAbrirFAD01}
          classeNoPrint="qp-noprint"
          tentativaEm={auditoria.tentativaEm}
        />

        <div className="qp-titulo-impressao" style={{ display: 'none', marginBottom: 12, color: '#000' }}>
          <div style={{ fontSize: 18, fontWeight: 700 }}>Quadro de Positividade por Tipo de Depósito</div>
          <div style={{ fontSize: 13 }}>Área: {areaNome} · {cicloNome} · {faixaSe}</div>
        </div>

        <div className="qp-noprint" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8, marginBottom: 12 }}>
          {passo(1, '#0072B2', '#dcebfa', 'Escolha a área e as semanas')}
          {passo(2, '#1B7F5B', '#dcf3ec', 'Confira se pode transcrever')}
          {passo(3, '#6A3D9A', '#efe7fa', 'Passe para o papel')}
        </div>

        <div className="qp-noprint" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          <button style={BOTAO} onClick={() => setFiltrosAbertos(f => !f)}>
            {filtrosAbertos ? '▴ Esconder filtros' : '▾ Mudar filtros'}
          </button>
          <button style={BOTAO} onClick={() => window.print()} disabled={!dados || !linhas.length}>🖨 Imprimir</button>
        </div>

        {filtrosAbertos && (
          <div className="qp-noprint" style={{ ...CARD, background: K.filtroBg, borderLeft: `6px solid ${K.filtroBorda}` }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12 }}>
              <label><span style={ROTULO}>📍 Área (obrigatória)</span>
                <select style={SELECT} value={areaId} onChange={e => setAreaId(e.target.value)}>
                  <option value="">Escolha a área</option>
                  {opcoes?.areas.map(a => <option key={a.id} value={String(a.id)}>{a.nome}</option>)}
                </select>
              </label>
              <label><span style={ROTULO}>🔁 Ciclo</span>
                <select style={SELECT} value={cicloId} onChange={e => setCicloId(e.target.value)}>
                  <option value="">Todos os ciclos</option>
                  {opcoes?.ciclos.map(c => <option key={c.id} value={String(c.id)}>{c.nome}</option>)}
                </select>
              </label>
              <label><span style={ROTULO}>📅 SE inicial</span>
                <select style={SELECT} value={seIni} onChange={e => setSeIni(e.target.value)}>
                  <option value="">Desde o início</option>
                  {opcoes?.semanas.map(s => <option key={s.se} value={String(s.se)}>SE {s.se} · {fmtData(s.inicio)} a {fmtData(s.fim)}</option>)}
                </select>
              </label>
              <label><span style={ROTULO}>📅 SE final</span>
                <select style={SELECT} value={seFim} onChange={e => setSeFim(e.target.value)}>
                  <option value="">Até a atual</option>
                  {opcoes?.semanas.map(s => <option key={s.se} value={String(s.se)}>SE {s.se} · {fmtData(s.inicio)} a {fmtData(s.fim)}</option>)}
                </select>
              </label>
            </div>
          </div>
        )}

        {erro && (
          <div className="qp-noprint" style={{ ...CARD, background: K.erroBg, borderLeft: `6px solid ${K.erroBorda}`, color: K.erroTx, fontSize: 15, fontWeight: 600 }}>
            {erro}
          </div>
        )}

        {semAreas && (
          <div style={{ ...CARD, background: K.infoBg, borderLeft: `6px solid ${K.infoBorda}`, fontSize: 15, lineHeight: 1.6 }}>
            <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 4 }}>Ainda não há dados para o quadro</div>
            Nenhuma área tem boletins a partir da SE {opcoes?.corte.se} ({fmtData(opcoes?.corte.data)}).
            {opcoes?.ultimo_boletim && <> Último boletim no sistema: {fmtData(opcoes.ultimo_boletim.data)} (SE {opcoes.ultimo_boletim.se}).</>}
            <br />Assim que os boletins forem enviados, a área aparece aqui.
          </div>
        )}

        {!semAreas && !areaId && opcoes && (
          <div style={{ ...CARD, background: K.infoBg, borderLeft: `6px solid ${K.infoBorda}`, fontSize: 15, fontWeight: 600 }}>
            📍 Escolha a área para ver o quadro.
          </div>
        )}

        {carregando && (
          <div style={{ ...CARD, background: K.infoBg, borderLeft: `6px solid ${K.infoBorda}`, fontSize: 15 }}>Carregando o quadro…</div>
        )}

        {!carregando && dados && linhas.length > 0 && (() => {
          // Passo 2 (Bloco F1): usa as amostras do quadro E a auditoria do modulo.
          const cartao = (bg: string, borda: string, tx: string, icone: string, titulo: string, texto: string) => (
            <div className="qp-noprint" style={{ ...CARD, background: bg, borderLeft: `6px solid ${borda}`, color: tx, display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 26 }} aria-hidden="true">{icone}</span>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800 }}>{titulo}</div>
                <div style={{ fontSize: 14 }}>{texto}</div>
              </div>
            </div>
          );
          const semRes = dados.amostras.sem_resultado;
          if (auditoria.estado === 'falhou' || auditoria.estado === 'carregando')
            return cartao(K.infoBg, K.infoBorda, K.tinta, '⏳', 'Aguarde a auditoria', 'Sem a conferência, o sistema não diz se pode transcrever.');
          if (audTravas > 0 || audRecusas > 0)
            return cartao(K.erroBg, K.erroBorda, K.erroTx, '⛔', 'Não transcreva ainda', 'Há erro no resultado de laboratório. Veja o quadro de avisos acima e corrija no FAD-01.');
          if (semRes > 0)
            return cartao(K.erroBg, K.erroBorda, K.erroTx, '⛔',
              `Não transcreva ainda — ${semRes} ${semRes === 1 ? 'amostra sem resultado' : 'amostras sem resultado'}`,
              `${semRes} ${semRes === 1 ? 'amostra ainda está' : 'amostras ainda estão'} sem resultado. Os números podem mudar.`);
          if (audAvisos > 0)
            return cartao(K.espereBg, K.espereBorda, K.espereTx, '⚠️', 'Pode transcrever, mas confira',
              `Não há erro. Há ${audAvisos} ${audAvisos === 1 ? 'ponto' : 'pontos'} no quadro de avisos que vale olhar antes de passar para o papel.`);
          if (dados.amostras.coletadas > 0)
            return cartao(K.okBg, K.okBorda, K.okTx, '✅', 'Pode transcrever',
              dados.amostras.coletadas === 1 ? 'A amostra deste filtro já tem resultado.' : `Todas as ${dados.amostras.coletadas} amostras deste filtro já têm resultado.`);
          return (
            <div className="qp-noprint" style={{ ...CARD, background: K.infoBg, borderLeft: `6px solid ${K.infoBorda}`, fontSize: 15, fontWeight: 600 }}>
              ℹ️ Nenhuma amostra foi coletada neste filtro.
            </div>
          );
        })()}

        {!carregando && dados && linhas.length === 0 && (
          <div style={{ ...CARD, background: K.infoBg, borderLeft: `6px solid ${K.infoBorda}`, fontSize: 15 }}>
            Nenhum boletim desta área para os filtros escolhidos.
          </div>
        )}

        {temTabela && (
          <>
            <div className="qp-noprint" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
              <button style={modo ? BOTAO : BOTAO_PRI} onClick={() => { setModo(m => !m); setDetalhe(null); }}>
                {modo ? '✕ Sair do modo' : '✍️ Modo passar para o papel'}
              </button>
              {modo && (
                <>
                  <button style={BOTAO} onClick={() => setIdx(i => Math.max(i - 1, 0))} aria-label="Linha anterior">▲</button>
                  <button style={BOTAO} onClick={() => setIdx(i => Math.min(i + 1, linhas.length - 1))} aria-label="Próxima linha">▼</button>
                  <button style={{ ...BOTAO, borderColor: '#1B7F5B', color: '#1B7F5B' }} onClick={passei}>✓ Passei esta linha</button>
                  <span style={{ fontSize: 15, fontWeight: 700, background: '#ffffff', border: `2px solid ${K.bordaFolha}`, borderRadius: 8, padding: '6px 10px' }}>
                    Linha {idx + 1} de {linhas.length} · {passadas} {passadas === 1 ? 'passada' : 'passadas'}
                  </span>
                  <span style={{ fontSize: 13, color: K.sub }}>(teclado: ↑ ↓ · Enter marca · Esc sai)</span>
                </>
              )}
              {passadas > 0 && <button style={{ ...BOTAO, fontSize: 13 }} onClick={limparMarcas}>Limpar marcações</button>}
            </div>

            <div className="qp-folha" style={{ background: K.folha, border: `1px solid ${K.bordaFolha}`, borderRadius: 12, padding: 14, boxShadow: '0 2px 10px rgba(16,35,63,0.08)' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', minWidth: 860, borderCollapse: 'collapse', tableLayout: 'fixed', border: `2px solid ${K.forte}` }}>
                  <colgroup>
                    <col style={{ width: 92 }} />
                    <col style={{ width: 96 }} />
                    {TIPOS.map(t => <col key={t} />)}
                    <col /><col /><col />
                  </colgroup>
                  <thead>
                    <tr>
                      <th rowSpan={2} style={{ ...TH, background: K.idBg }}>Ciclo</th>
                      <th rowSpan={2} style={{ ...TH, background: K.idBg }}>SE</th>
                      {FAMILIAS.map(f => (
                        <th key={f} colSpan={FAM[f].span} style={{ ...TH, background: FAM[f].cab, color: FAM[f].cabTx, borderLeft: BORDA_FAMILIA }}>
                          <div style={{ fontSize: 16 }}>{f}</div>
                          <div style={{ fontSize: 11, fontWeight: 600 }}>{FAM[f].nome}</div>
                        </th>
                      ))}
                      <th rowSpan={2} style={{ ...TH, background: TOT.pos.bg, color: TOT.pos.tx, borderLeft: BORDA_FAMILIA, fontSize: 15 }}>{TOT.pos.rotulo}</th>
                      <th rowSpan={2} style={{ ...TH, background: TOT.imo.bg, color: TOT.imo.tx, fontSize: 15 }}>{TOT.imo.rotulo}</th>
                      <th rowSpan={2} style={{ ...TH, background: TOT.qt.bg, color: TOT.qt.tx, fontSize: 15 }}>{TOT.qt.rotulo}</th>
                    </tr>
                    <tr>
                      {TIPOS.map(t => {
                        const f = FAM[FAMILIA_DO_TIPO[t]];
                        return (
                          <th key={t} style={{ ...TH, background: f.cab, color: f.cabTx, borderLeft: INICIO_FAMILIA.has(t) ? BORDA_FAMILIA : BORDA }}>
                            {t.toUpperCase()}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {linhas.map((l, i) => {
                      const foco = modo && i === idx;
                      const aberta = !modo && detalhe === l.chave;
                      const feita = marcada(l);
                      const extra: React.CSSProperties = {
                        ...(l.tipo === 'total' ? { borderTop: `4px solid ${K.forte}` } : {}),
                        ...(l.tipo === 'geral' ? { borderTop: `6px double ${K.forte}`, fontSize: 22 } : {}),
                        ...(foco || aberta ? { boxShadow: `inset 0 0 0 999px ${K.marca}` } : {}),
                      };
                      const clicavel = l.tipo === 'semana' || modo;
                      return (
                        <tr
                          key={l.chave}
                          onClick={() => {
                            if (modo) { setIdx(i); return; }
                            if (l.tipo === 'semana') setDetalhe(d => (d === l.chave ? null : l.chave));
                          }}
                          style={{ cursor: clicavel ? 'pointer' : 'default' }}
                        >
                          <td style={{ ...TD, ...extra, background: feita ? '#dcf3ec' : K.idBg, fontFamily: 'inherit', fontSize: l.tipo === 'semana' ? 17 : 15 }}>
                            {l.rotuloCiclo}{feita && <span style={{ color: '#1B7F5B' }}> ✓</span>}
                          </td>
                          <td style={{ ...TD, ...extra, background: feita ? '#dcf3ec' : K.idBg, padding: '6px 3px' }}>
                            <div>{l.rotuloSe}</div>
                            {l.periodo && <div style={{ fontSize: 12, fontWeight: 600, color: K.sub, fontFamily: 'inherit' }}>{l.periodo}</div>}
                          </td>
                          {renderValores(l.valores, extra)}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="qp-noprint" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
                {FAMILIAS.map(f => (
                  <span key={f} style={{ ...CHIP, background: FAM[f].cab, color: FAM[f].cabTx }}>{LEGENDA_FAM[f]}</span>
                ))}
              </div>
              <div className="qp-noprint" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                {(['pos', 'imo', 'qt'] as Total[]).map(g => (
                  <span key={g} style={{ ...CHIP, background: TOT[g].bg, color: TOT[g].tx }}>{TOT[g].legenda}</span>
                ))}
                <span style={{ ...CHIP, background: K.idBg, color: K.sub }}>– = zero</span>
              </div>
              {!modo && (
                <div className="qp-noprint" style={{ marginTop: 8, fontSize: 13, color: K.sub, fontWeight: 600 }}>
                  👆 Clique numa semana para ver os boletins que formam o número.
                </div>
              )}
            </div>

            {semanaDetalhe && !modo && (
              <div className="qp-noprint" style={{ ...CARD, marginTop: 14, background: '#ffffff', border: `2px solid ${K.marcaBorda}`, borderLeft: `8px solid ${K.marcaBorda}` }}>
                <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 4 }}>
                  🔎 SE {semanaDetalhe.se} · {fmtData(semanaDetalhe.se_inicio)} a {fmtData(semanaDetalhe.se_fim)}
                </div>
                <div style={{ fontSize: 14, color: K.sub, marginBottom: 10, fontWeight: 600 }}>
                  {semanaDetalhe.total_boletins_semana} boletins na semana · {semanaDetalhe.boletins.length} com positivo ·
                  quarteirões positivos: {semanaDetalhe.quarteiroes.length ? semanaDetalhe.quarteiroes.join(', ') : 'nenhum'}
                </div>
                {semanaDetalhe.boletins.length > 0 && (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                      <thead>
                        <tr>
                          {['Boletim', 'Data', 'Agente', 'Qt trabalhado', 'Depósitos', 'Dep+', 'Imóv+', 'Qt positivos'].map(h => (
                            <th key={h} style={{ ...TH, fontSize: 12, background: K.idBg }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {semanaDetalhe.boletins.map(b => (
                          <tr key={b.id}>
                            <td style={{ ...TD, fontSize: 14 }}>#{b.id}</td>
                            <td style={{ ...TD, fontSize: 14 }}>{fmtData(b.data)}</td>
                            <td style={{ ...TD, fontSize: 14, fontFamily: 'inherit', textAlign: 'left' }}>{b.agente || '—'}</td>
                            <td style={{ ...TD, fontSize: 14 }}>{b.quarteirao_trabalhado || '—'}</td>
                            <td style={{ ...TD, fontSize: 13, textAlign: 'left' }}>
                              {TIPOS.filter(t => b.dep[t] > 0).map(t => `${t.toUpperCase()} ${b.dep[t]}`).join(' · ') || '–'}
                            </td>
                            <td style={{ ...TD, fontSize: 14, background: TOT.pos.bg, color: TOT.pos.tx }}>{val(b.dep_pos)}</td>
                            <td style={{ ...TD, fontSize: 14, background: TOT.imo.bg, color: TOT.imo.tx }}>{val(b.imov_pos)}</td>
                            <td style={{ ...TD, fontSize: 14, background: TOT.qt.bg, color: TOT.qt.tx }}>{b.quarteiroes_positivos.join(', ') || '–'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {dados && (
              <div style={{ marginTop: 14, fontSize: 12, color: K.sub }}>
                Amostras no filtro: {dados.amostras.coletadas} coletadas · {dados.amostras.sem_resultado} sem resultado · {dados.amostras.congeladas} congeladas ·
                gerado em {new Date(dados.gerado_em).toLocaleString('pt-BR')}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
